import {
  Bell,
  Calendar,
  CheckSquare,
  ClipboardCheck,
  Clock,
  FileText,
  HardHat,
  LayoutDashboard,
  type LucideIcon,
  MessageSquare,
  Package,
  Map,
  Palette,
  Phone,
  Receipt,
  Settings,
  Sparkles,
  TrendingUp,
  Truck,
  UserCog,
  Users,
} from 'lucide-react';

/**
 * Familles du menu, dans l'ordre d'affichage. Rangées par USAGE et non par
 * ordre de développement : ce qu'on ouvre depuis le toit ou la camionnette
 * d'abord, la paperasse ensuite, les réglages en dernier.
 */
export type GroupeModule = 'terrain' | 'commerce' | 'gestion' | 'admin';

export const GROUPES: readonly { id: GroupeModule; label: string }[] = [
  { id: 'terrain', label: 'Terrain' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'gestion', label: 'Gestion' },
  { id: 'admin', label: 'Admin' },
];

export interface AppModule {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  ready: boolean;
  groupe: GroupeModule;
  /** Candidat à la barre d'onglets du téléphone (les quatre premiers autorisés y vont). */
  onglet?: boolean;
  /** Libellé court pour l'onglet, quand le nom complet ne tient pas sous une icône. */
  court?: string;
  /**
   * Hors du menu, mais l'écran existe : on y arrive autrement. Les alertes par
   * le bloc « À faire aujourd'hui » du tableau de bord ; l'assistant par son
   * bouton en haut de l'écran.
   */
  horsMenu?: boolean;
}

// Modules de l'application + ordre dans la navigation. `ready` passe à true
// au fur et à mesure des phases.
export const MODULES: readonly AppModule[] = [
  // ----- Terrain : ce qu'on ouvre entre deux échelles -------------------------
  {
    id: 'dashboard',
    label: 'Tableau de bord',
    court: 'Accueil',
    path: '/',
    icon: LayoutDashboard,
    ready: true,
    groupe: 'terrain',
    onglet: true,
  },
  // Écran d'appel des devis sans réponse, pensé pour le téléphone. Au premier
  // niveau du menu : c'est un outil de terrain, pas un sous-écran de pilotage.
  {
    id: 'relances',
    label: 'À relancer',
    path: '/relances',
    icon: Phone,
    ready: true,
    groupe: 'terrain',
    onglet: true,
  },
  { id: 'agenda', label: 'Agenda', path: '/agenda', icon: Calendar, ready: true, groupe: 'terrain', onglet: true },
  {
    id: 'chantiers',
    label: 'Chantiers',
    path: '/chantiers',
    icon: HardHat,
    ready: true,
    groupe: 'terrain',
    onglet: true,
  },
  // Tableau d'organisation des chantiers : une ligne par chantier, une colonne
  // par étape à préparer. Le premier écran qu'on ouvre le lundi matin.
  {
    id: 'suivi',
    label: 'Suivi chantiers',
    court: 'Suivi',
    path: '/suivi',
    icon: ClipboardCheck,
    ready: true,
    groupe: 'terrain',
  },
  // La liste des choses à ne pas oublier, rangée par échéance. Au terrain et
  // non en gestion : on l'ouvre entre deux chantiers, pas au bureau le soir.
  { id: 'taches', label: 'À faire', path: '/taches', icon: CheckSquare, ready: true, groupe: 'terrain' },
  // Carte des communes démarchées : où les commerciaux sont passés, et surtout
  // où ils ne sont pas passés depuis trop longtemps.
  { id: 'prospection', label: 'Prospection', path: '/prospection', icon: Map, ready: true, groupe: 'terrain' },
  // ----- Commerce ---------------------------------------------------------------
  { id: 'clients', label: 'Clients', path: '/clients', icon: Users, ready: true, groupe: 'commerce', onglet: true },
  { id: 'devis', label: 'Devis', path: '/devis', icon: FileText, ready: true, groupe: 'commerce' },
  { id: 'factures', label: 'Factures', path: '/factures', icon: Receipt, ready: true, groupe: 'commerce' },
  // ----- Gestion ----------------------------------------------------------------
  { id: 'pilotage', label: 'Pilotage', path: '/pilotage', icon: TrendingUp, ready: true, groupe: 'gestion' },
  { id: 'alertes', label: 'Alertes', path: '/alertes', icon: Bell, ready: true, groupe: 'gestion', horsMenu: true },
  { id: 'messagerie', label: 'Messagerie', path: '/messagerie', icon: MessageSquare, ready: true, groupe: 'gestion' },
  {
    id: 'assistant',
    label: 'Assistant',
    path: '/assistant',
    icon: Sparkles,
    ready: true,
    groupe: 'gestion',
    horsMenu: true,
  },
  { id: 'rh', label: 'RH', path: '/rh', icon: Clock, ready: true, groupe: 'gestion' },
  // Parc roulant : carnet d'entretien, échéances (CT, assurance, révision) et
  // ce que chaque véhicule coûte.
  { id: 'vehicules', label: 'Véhicules', path: '/vehicules', icon: Truck, ready: true, groupe: 'gestion' },
  // ----- Admin ------------------------------------------------------------------
  // On y va rarement : la liste des prestations sert surtout à bâtir les devis.
  { id: 'catalogue', label: 'Catalogue', path: '/prestations', icon: Package, ready: true, groupe: 'admin' },
  { id: 'parametres', label: 'Paramètres', path: '/parametres', icon: Settings, ready: true, groupe: 'admin' },
  {
    id: 'personnalisation',
    label: 'Personnalisation',
    path: '/personnalisation',
    icon: Palette,
    ready: true,
    groupe: 'admin',
  },
  { id: 'utilisateurs', label: 'Utilisateurs', path: '/utilisateurs', icon: UserCog, ready: true, groupe: 'admin' },
];
